/* =============================================================================
   VIEWS / ADMIN / ORDERS-NOTIFICATIONS — Sound + Push + Counter (Phase 6)
   =============================================================================
   Drei Side-Effects bei neuen Order-Realtime-Events:
     1. Akustisches Signal (Web Audio API, kein Asset nötig). Sound ist
        per Default an, Toggle persistiert im localStorage.
     2. Browser-Push-Notification — opt-in via Button im Cockpit-Header.
        Permission wird NUR auf User-Click angefragt, NICHT automatisch.
     3. Live-Counter im Tab-Button + Toolbar (Anzahl offener Bestellungen).

   Außerdem: Pulse-Animation an neuen Karten (Klasse .order-fresh) — die
   wird in orders.js beim Rendern gesetzt und durch orderMarkSeen entfernt.

   Verkabelt sich beim Boot über die App-Event-Bus-Events:
     • order:realtime         — Cache-Patch ist bereits durch, hier Side-Effects
     • order:status_changed   — Counter aktualisieren
     • order:cancelled        — Counter aktualisieren
     • order:created          — (auch noch nicht via Realtime gesehen, wenn
                                 lokaler Submit) — kein Sound bei eigener
                                 Aktion, daher kein Hook hier.
============================================================================ */

import { dateToISO } from '../../utils/helpers.js';

const LS_SOUND   = 'gg.orders.notifySound';
const LS_PUSH    = 'gg.orders.notifyPush';

let _audioCtx = null;
let _initialized = false;

// -----------------------------------------------------------------------------
// Settings (localStorage)
// -----------------------------------------------------------------------------

function isSoundEnabled() {
  const v = localStorage.getItem(LS_SOUND);
  // Default: an. Nur explizites "0" deaktiviert.
  return v !== '0';
}
function setSoundEnabled(on) {
  localStorage.setItem(LS_SOUND, on ? '1' : '0');
}
function isPushEnabled() {
  return localStorage.getItem(LS_PUSH) === '1'
         && 'Notification' in window
         && Notification.permission === 'granted';
}

// -----------------------------------------------------------------------------
// Sound — Web Audio API "Ping"
// -----------------------------------------------------------------------------

/** Lazy initialisierter AudioContext (Browser brauchen User-Gesture vor dem
 *  ersten Sound — wir öffnen den Kontext erst beim ersten Klick). */
function getAudioCtx() {
  if (_audioCtx) return _audioCtx;
  try {
    const Ctor = window.AudioContext || window.webkitAudioContext;
    if (!Ctor) return null;
    _audioCtx = new Ctor();
    return _audioCtx;
  } catch (_) { return null; }
}

/** Kurzer freundlicher Doppel-Ping (zwei Sinustöne mit Decay). */
export function playOrderPing() {
  if (!isSoundEnabled()) return;
  const ctx = getAudioCtx();
  if (!ctx) return;
  try {
    if (ctx.state === 'suspended') ctx.resume();
    const now = ctx.currentTime;
    [{ f: 880, t: 0 }, { f: 1320, t: 0.16 }].forEach(({ f, t }) => {
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.type = 'sine';
      osc.frequency.value = f;
      gain.gain.setValueAtTime(0.0001, now + t);
      gain.gain.exponentialRampToValueAtTime(0.18, now + t + 0.02);
      gain.gain.exponentialRampToValueAtTime(0.0001, now + t + 0.35);
      osc.connect(gain).connect(ctx.destination);
      osc.start(now + t);
      osc.stop(now + t + 0.4);
    });
  } catch (e) {
    console.warn('[orders-notify] sound playback failed:', e);
  }
}

// -----------------------------------------------------------------------------
// Browser-Push
// -----------------------------------------------------------------------------

/** Push einschalten/ausschalten. Bei "einschalten" wird Permission angefragt
 *  (User-Gesture vorausgesetzt — der Button-Klick reicht). */
export async function toggleOrderPushNotifications() {
  if (!('Notification' in window)) {
    return showOrErr('Browser-Benachrichtigungen werden hier nicht unterstützt.');
  }
  if (isPushEnabled()) {
    // Aus: localStorage-Flag löschen (Permission selbst kann der Browser-
    // User nur über Browser-Settings widerrufen).
    localStorage.setItem(LS_PUSH, '0');
    refreshOrdersPushButton();
    if (window.showToast) window.showToast('Push-Benachrichtigungen deaktiviert', 'success');
    return;
  }
  // An: Permission anfragen
  let perm = Notification.permission;
  if (perm === 'default') {
    try {
      perm = await Notification.requestPermission();
    } catch (_) {}
  }
  if (perm === 'granted') {
    localStorage.setItem(LS_PUSH, '1');
    // Test-Benachrichtigung, damit der User sieht, dass es funktioniert
    try { new Notification('Grill & Green', { body: 'Benachrichtigungen sind aktiv.' }); } catch (_) {}
    refreshOrdersPushButton();
    if (window.showToast) window.showToast('Push-Benachrichtigungen aktiviert', 'success');
  } else if (perm === 'denied') {
    showOrErr('Benachrichtigungen blockiert — bitte in den Browser-Einstellungen für diese Seite erlauben.');
  } else {
    refreshOrdersPushButton();
  }
}

function showOrErr(msg) {
  // S190 — kein native alert mehr: Toast ist der Kanal; ohne Toast nur Console
  // (kommt praktisch nie vor, showToast ist global registriert).
  if (window.showToast) window.showToast(msg, 'error');
  else console.error('[orders-notifications]', msg);
}

/** Zeigt eine Browser-Notification für eine neue Bestellung. */
function maybeShowOrderPush(order) {
  if (!isPushEnabled()) return;
  // Wenn der Tab aktiv im Vordergrund ist, schicken wir KEINE Push —
  // visueller Pulse + Sound reichen. Push ist für "Tab im Hintergrund".
  if (document.visibilityState === 'visible') return;
  const num    = order && (order.orderNumber || order.order_number || '');
  const name   = order && (order.customerName || order.customer_name || 'Gast');
  const pickup = order && (order.pickupTime || order.pickup_time);
  const pickupShort = typeof pickup === 'string' ? pickup.slice(0, 5) : '';
  try {
    new Notification('Neue Abhol-Bestellung', {
      body: `${name} — Abholung ${pickupShort || 'jetzt'}${num ? ' · ' + num : ''}`,
      tag: 'order-' + (order && order.id)
    });
  } catch (e) {
    console.warn('[orders-notify] push failed:', e);
  }
}

// -----------------------------------------------------------------------------
// Button-State im Cockpit-Header
// -----------------------------------------------------------------------------

export function refreshOrdersPushButton() {
  const btn = document.getElementById('orders-notify-toggle');
  if (!btn) return;
  if (!('Notification' in window)) {
    btn.style.display = 'none';
    return;
  }
  const perm = Notification.permission;
  const on = isPushEnabled();
  if (on) {
    btn.innerHTML = '🔔 Aktiv';
    btn.classList.add('active');
    btn.title = 'Push-Benachrichtigungen sind aktiv — klicken zum Deaktivieren';
  } else if (perm === 'denied') {
    btn.innerHTML = '🔕 Blockiert';
    btn.classList.remove('active');
    btn.title = 'Browser hat Benachrichtigungen blockiert. In den Browser-Einstellungen aktivieren.';
  } else {
    btn.innerHTML = '🔔 Aktivieren';
    btn.classList.remove('active');
    btn.title = 'Browser-Benachrichtigungen bei neuer Bestellung aktivieren';
  }
}

// -----------------------------------------------------------------------------
// Live-Counter (Tab-Pille im Tab-Button)
// -----------------------------------------------------------------------------

export function refreshOrdersTabCounter() {
  const pill = document.getElementById('orders-tab-counter');
  if (!pill) return;
  const state = (window.App && window.App.storage && window.App.storage._getInternalState())
    || { orders: [] };
  const today = dateToISO(new Date()); // S61 FIX.4: TZ-konsistent statt UTC-Slice
  // Phase 22 (Session 22 Nachschlag): geteilte Status-Klassifizierung statt
  // eigener Filter-Liste. Bisher: ['pending','preparing'] → zu eng (confirmed
  // fehlt) und gleichzeitig irreführend (preparing = KDS/Bar arbeitet, kein
  // Bediener-Action-Bedarf). Jetzt: nur Orders die wirklich auf Bediener
  // warten (pending = annehmen, ready = ausgeben).
  const helpers = (window.OrderStatuses && window.OrderStatuses.isOrderAwaitingOperator)
    ? window.OrderStatuses
    : null;
  const isAwaiting = helpers ? helpers.isOrderAwaitingOperator : (o) => o && (o.status === 'pending' || o.status === 'ready');
  const openCount = (state.orders || []).filter(o => {
    if (!o) return false;
    const date = o.pickupDate || o.pickup_date;
    if (!date || date < today) return false;
    return isAwaiting(o);
  }).length;
  if (openCount > 0) {
    pill.style.display = '';
    pill.textContent = String(openCount);
  } else {
    pill.style.display = 'none';
  }
}

// -----------------------------------------------------------------------------
// Init (vom bootstrap.js aufgerufen)
// -----------------------------------------------------------------------------

export function initOrderNotifications() {
  if (_initialized) return;
  _initialized = true;
  const App = window.App;
  if (!App || !App.events) return;

  // Bei jedem Realtime-INSERT: Sound + Push + Counter aktualisieren.
  // Bei UPDATE/DELETE: nur Counter aktualisieren.
  App.events.on('order:realtime', payload => {
    try {
      if (!payload) return;
      if (payload.eventType === 'INSERT' && payload.new) {
        // Nur neue eingehende Bestellungen feuern Sound/Push (status='pending').
        if (payload.new.status === 'pending') {
          // Session 19+20 (Phase 15/16.3): Wenn KDS oder Bar-View aktiv sind,
          // übernehmen die ihren eigenen Sound — Cockpit skippt, sonst Doppel-Ping.
          if (!document.body.classList.contains('kds-active') &&
              !document.body.classList.contains('bar-active')) {
            playOrderPing();
          }
          // Push-Notif geht trotzdem raus, weil der Operator nicht zwingend
          // an einer der beiden Sichten sitzt (z.B. Cockpit-Browser-Tab im
          // Hintergrund + KDS-Tablet in der Küche → Push ist die Insurance).
          maybeShowOrderPush({
            id:           payload.new.id,
            orderNumber:  payload.new.order_number,
            customerName: payload.new.customer_name,
            pickupTime:   payload.new.pickup_time
          });
        }
      }
      refreshOrdersTabCounter();
    } catch (e) {
      console.warn('[orders-notify] order:realtime handler:', e);
    }
  });

  // Status-Wechsel + Cancel: Counter neu rechnen
  App.events.on('order:status_changed', () => refreshOrdersTabCounter());
  App.events.on('order:cancelled',      () => refreshOrdersTabCounter());
  App.events.on('order:created',        () => refreshOrdersTabCounter());

  // Initial-Sync nach Login (Cache ist da, Tab-Counter kann jetzt rechnen)
  refreshOrdersTabCounter();
}

// -----------------------------------------------------------------------------
// Backward-Compat (inline-onclick + bootstrap-Hooks)
// -----------------------------------------------------------------------------

if (typeof window !== 'undefined') {
  Object.assign(window, {
    toggleOrderPushNotifications,
    refreshOrdersPushButton,
    refreshOrdersTabCounter,
    initOrderNotifications,
    playOrderPing
  });
}
